import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { enqueueSnackbar } from '~/utils/notifications';
import { isTextEntryElement } from '~/utils/textEntry';
import type { PlaneCycleOutcome } from '~/viewportGrid/layout/planeCycle';

/**
 * The key that cycles the planes of a row. `Ctrl+R` is the Eclipse binding,
 * so users arriving from Eclipse keep their muscle memory; bare `R` is
 * offered as the simpler equivalent. Both are handled identically.
 */
const CYCLE_KEY = 'r';

/**
 * Hook: listen for R / Ctrl+R and cycle the planes of one row through
 * `cyclePlanes`, which picks the row, cycles it and reports the outcome.
 * A refusal shows as a warning with its reason.
 *
 * Guards (early-return so other handlers and the browser still process the
 * event):
 *  - Cmd/Alt/Shift held: preserves OS/browser shortcuts. Ctrl is NOT a
 *    disqualifier here because Ctrl+R is one of this hook's own bindings.
 *  - The event is an auto-repeat (avoids racing through planes when the
 *    key is held down).
 *  - The active element takes typed text (the user is typing).
 *  - `cyclePlanes` ignores the key because a tool action is in progress
 *    (e.g. a half-drawn line).
 *
 * Ctrl+R is kept from the browser (a page reload) when the key cycles the
 * planes and when it is refused; on every other path it keeps its normal
 * browser meaning. `cyclePlanes` should be a stable callback: a new one
 * re-subscribes the listener.
 */
export function usePlaneCycleShortcut(
    cyclePlanes: () => PlaneCycleOutcome
): void {
    const intl = useIntl();

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key.toLowerCase() !== CYCLE_KEY) {
                return;
            }
            if (event.metaKey || event.altKey || event.shiftKey) {
                return;
            }
            if (event.repeat) {
                return;
            }
            if (isTextEntryElement(document.activeElement)) {
                return;
            }
            const outcome = cyclePlanes();
            if (outcome.kind === 'ignored') {
                return;
            }
            if (event.ctrlKey) {
                event.preventDefault();
            }
            if (outcome.kind === 'refused') {
                enqueueSnackbar(intl.formatMessage({ id: outcome.messageId }), {
                    variant: 'warning',
                });
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [cyclePlanes, intl]);
}
